// src/pages/MentorsBooking.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, updateDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { FaExclamationTriangle } from "react-icons/fa";
import {
  FiVideo,
  FiMessageCircle,
  FiPhoneCall,
  FiCalendar,
  FiClock,
  FiUser,
  FiCheck,
  FiSearch,
  FiFilter,
} from "react-icons/fi";

/* ────────────────────────────────────────────────────────── */
/* Colors                                                     */
/* ────────────────────────────────────────────────────────── */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
};

const STATUS_RANK = { pending: 0, accepted: 1, completed: 2 };

/* status → chip colors */
function statusChipStyle(status) {
  const base = {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    display: "inline-block",
    border: "1px solid transparent",
  };
  if (status === "pending")
    return { ...base, color: "#92400e", background: "#fef3c7", borderColor: "#fcd34d" };
  if (status === "accepted")
    return { ...base, color: "#1e3a8a", background: "#dbeafe", borderColor: "#93c5fd" };
  if (status === "completed")
    return { ...base, color: "#065f46", background: "#d1fae5", borderColor: "#86efac" };
  return { ...base, color: COLORS.text, background: "#f1f5f9", borderColor: "#e2e8f0" };
}

/* ────────────────────────────────────────────────────────── */
/* Component                                                  */
/* ────────────────────────────────────────────────────────── */
export default function MentorsBooking() {
  const [hasPermission, setHasPermission] = useState(true);
  const [canEdit, setCanEdit] = useState(false);

  const [videoRows, setVideoRows] = useState([]);
  const [chatRows, setChatRows] = useState([]);
  const [audioRows, setAudioRows] = useState([]);

  const [activeTab, setActiveTab] = useState("video"); // "video" | "chat" | "audio"
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [changing, setChanging] = useState({}); // { "<coll>:<id>": boolean }

  // UI controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | pending | accepted | completed

  // ── permission
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("employee"));
    const role = user?.role;
    const perms = user?.permissions || [];
    const allowedView = role === "super_admin" || perms.includes("view_mentor_bookings");
    const allowedEdit =
      role === "super_admin" ||
      perms.includes("manage_mentor_bookings") ||
      perms.includes("edit_mentor_bookings");
    setHasPermission(allowedView);
    setCanEdit(allowedEdit);
  }, []);

  // ── live subscriptions (ordered by createdAt desc if present)
  useEffect(() => {
    if (!hasPermission) return;

    const unsubs = [];

    unsubs.push(
      onSnapshot(query(collection(db, "video_request_mentor"), orderBy("createdAt", "desc")), (snap) => {
        const list = snap.docs.map((d) => makeRow("video_request_mentor", d.id, d.data(), "video"));
        setVideoRows(sortBookings(list));
        setLoading(false);
      })
    );

    unsubs.push(
      onSnapshot(query(collection(db, "chat_request_mentor"), orderBy("createdAt", "desc")), (snap) => {
        const list = snap.docs.map((d) => makeRow("chat_request_mentor", d.id, d.data(), "chat"));
        setChatRows(sortBookings(list));
        setLoading(false);
      })
    );

    unsubs.push(
      onSnapshot(query(collection(db, "call_request_mentor"), orderBy("createdAt", "desc")), (snap) => {
        const list = snap.docs.map((d) => makeRow("call_request_mentor", d.id, d.data(), "audio"));
        setAudioRows(sortBookings(list));
        setLoading(false);
      })
    );

    return () => unsubs.forEach((u) => u && u());
  }, [hasPermission]);

  // rows for current tab
  const rows = useMemo(() => {
    if (activeTab === "video") return videoRows;
    if (activeTab === "chat") return chatRows;
    return audioRows;
  }, [activeTab, videoRows, chatRows, audioRows]);

  // counts for header cards (current tab)
  const counts = useMemo(() => {
    const arr = rows;
    const total = arr.length;
    const pending = arr.filter((r) => r.status === "pending").length;
    const accepted = arr.filter((r) => r.status === "accepted").length;
    const completed = arr.filter((r) => r.status === "completed").length;
    return { total, pending, accepted, completed };
  }, [rows]);

  // filtered rows (search + status)
  const filteredRows = useMemo(() => {
    let list = rows;

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((b) => {
        const hay = `${b.userName || ""} ${b.userPhone || ""} ${b.mentorId || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (statusFilter !== "All") {
      list = list.filter((b) => b.status === statusFilter);
    }

    return list;
  }, [rows, search, statusFilter]);

  // ── actions
  async function applyStatus(coll, id, nextStatus) {
    const key = `${coll}:${id}`;
    try {
      setChanging((m) => ({ ...m, [key]: true }));
      await updateDoc(doc(db, coll, id), { status: String(nextStatus || "pending").toLowerCase() });
      showToast("Status updated");
    } catch (e) {
      console.error(e);
      showToast("Update failed. Try again.");
    } finally {
      setChanging((m) => ({ ...m, [key]: false }));
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 1600);
  }

  if (!hasPermission) {
    return (
      <div style={blurOverlay}>
        <div style={popupBox}>
          <FaExclamationTriangle size={24} color="#e53935" />
          <h3 style={{ marginTop: "0.5rem" }}>Access Denied</h3>
          <p>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={{ fontFamily: "Inter, system-ui, sans-serif", background: COLORS.page, minHeight: "100vh" }}>
      <div className="aa-pro-hero" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div>
          <h2 className="aa-pro-title">Mentor Bookings</h2>
          <p className="aa-pro-subtitle" style={{ marginTop: 6 }}>Track video, chat, and audio bookings in one live queue.</p>
        </div>
        <div className="aa-pro-segment" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <TabButton active={activeTab === "video"} onClick={() => setActiveTab("video")}>
            <FiVideo /> Video ({videoRows.length})
          </TabButton>
          <TabButton active={activeTab === "chat"} onClick={() => setActiveTab("chat")}>
            <FiMessageCircle /> Chat ({chatRows.length})
          </TabButton>
          <TabButton active={activeTab === "audio"} onClick={() => setActiveTab("audio")}>
            <FiPhoneCall /> Audio ({audioRows.length})
          </TabButton>
        </div>
      </div>

      <div className="aa-pro-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
        <StatCard label="Total" value={counts.total} />
        <StatCard label="Pending" value={counts.pending} tone="pending" />
        <StatCard label="Accepted" value={counts.accepted} tone="accepted" />
        <StatCard label="Completed" value={counts.completed} tone="completed" />
      </div>

      <div className="aa-pro-toolbar" style={controlsRow}>
        <div style={searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user, phone, or mentor id…"
            style={searchInput}
          />
        </div>
        <div style={controlGroup}>
          <FiFilter />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={select}>
            <option>All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="aa-pro-table-shell" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".95rem" }}>
          <thead>
            <tr>
              <Th sticky>Mode</Th>
              <Th sticky>User</Th>
              <Th sticky>Mentor ID</Th>
              <Th sticky>Date</Th>
              <Th sticky>Time</Th>
              <Th sticky>Amount</Th>
              <Th sticky>Status</Th>
              <Th sticky>Update</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={tdLoading}>Loading…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={8} style={tdLoading}>No records</td></tr>
            ) : (
              filteredRows.map((b, idx) => {
                const key = `${b.coll}:${b.id}`;
                const rowBg = idx % 2 ? "#fafafa" : "#fff";
                return (
                  <tr
                    key={key}
                    style={{
                      borderTop: `1px solid ${COLORS.border}`,
                      background: rowBg,
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                  >
                    {/* Mode: icon only */}
                    <Td>
                      {b.mode === "video" ? <FiVideo /> : b.mode === "chat" ? <FiMessageCircle /> : <FiPhoneCall />}
                    </Td>

                    {/* User */}
                    <Td>
                      <div style={{ display: "grid", gap: 2 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800 }}>
                          <FiUser /> {b.userName || "—"}
                        </div>
                        <div style={{ color: COLORS.subtext, fontSize: 12 }}>{b.userPhone || "—"}</div>
                      </div>
                    </Td>

                    {/* Mentor ID: toggle full/short */}
                    <Td>
                      <ToggleId text={b.mentorId || "—"} max={10} />
                    </Td>

                    {/* Date */}
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiCalendar /> {formatDateOnly(b.date) || "—"}
                      </div>
                    </Td>

                    {/* Time */}
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiClock /> {b.fromTime && b.toTime ? `${b.fromTime}–${b.toTime}` : "—"}
                      </div>
                    </Td>

                    {/* Amount */}
                    <Td>₹{formatAmount(b.amountPaid || b.totalPrice || 0)}</Td>

                    {/* Status pill + select */}
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={statusChipStyle(b.status)}>{cap(b.status)}</span>
                        <StatusSelect id={key} initial={b.status} disabled={!canEdit} />
                      </div>
                    </Td>

                    {/* Apply button */}
                    <Td>
                      <button
                        style={{ ...primaryBtn, opacity: canEdit ? 1 : 0.7 }}
                        disabled={!!changing[key] || !canEdit}
                        title={canEdit ? "Apply status" : "No permission"}
                        onClick={() => {
                          const el = document.getElementById(`status-${key}`);
                          const next = el ? el.value : "pending";
                          applyStatus(b.coll, b.id, next);
                        }}
                      >
                        <FiCheck /> {changing[key] ? "Updating…" : "Update"}
                      </button>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {toast && <div style={toastBox}>{toast}</div>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Small parts                                                */
/* ────────────────────────────────────────────────────────── */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        padding: "10px",
        textAlign: "left",
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td style={{ padding: "10px", color: COLORS.text, verticalAlign: "top" }}>{children}</td>;
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: "8px 12px",
        border: `1px solid ${active ? "transparent" : COLORS.border}`,
        background: active
          ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
          : "#fff",
        color: active ? "#fff" : COLORS.text,
        fontWeight: 800,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        boxShadow: "0 4px 10px rgba(0,0,0,.06)",
      }}
    >
      {children}
    </button>
  );
}

function StatusSelect({ id, initial, disabled }) {
  const [val, setVal] = useState(String(initial || "pending").toLowerCase());
  return (
    <select
      id={`status-${id}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      style={selectStyle}
      disabled={disabled}
    >
      <option value="pending">Pending</option>
      <option value="accepted">Accepted</option>
      <option value="completed">Completed</option>
    </select>
  );
}

/* Toggle full/short text for Mentor ID */
function ToggleId({ text, max = 10 }) {
  const [open, setOpen] = useState(false);
  if (!text || text === "—") return <span>—</span>;
  const short = text.length > max ? `${text.slice(0, max)}…` : text;
  return (
    <button
      onClick={() => setOpen((s) => !s)}
      title={open ? "Click to show less" : "Click to show full"}
      style={idBtnStyle}
    >
      {open ? text : short}
    </button>
  );
}

function StatCard({ label, value, tone }) {
  const palette =
    tone === "pending"
      ? { bg: "#fff7ed", border: "#fed7aa", text: "#92400e" }
      : tone === "accepted"
      ? { bg: "#eff6ff", border: "#bfdbfe", text: "#1e3a8a" }
      : tone === "completed"
      ? { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" }
      : { bg: "#fff", border: COLORS.border, text: COLORS.text };
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: tone ? palette.bg : "rgba(255,255,255,0.14)",
        border: `1px solid ${tone ? palette.border : "rgba(255,255,255,0.24)"}`,
        borderRadius: 14,
        padding: "10px 12px",
        minWidth: 120,
        display: "grid",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: tone ? COLORS.subtext : "rgba(255,255,255,0.76)" }}>{label}</div>
      <div style={{ fontWeight: 900, color: tone ? palette.text : "#fff", fontSize: 18 }}>{value}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Helpers                                                    */
/* ────────────────────────────────────────────────────────── */
function makeRow(coll, id, x = {}, expectedMode) {
  const createdAtMs = toMillis(x.createdAt);
  const status = String(x.status || "pending").toLowerCase();

  // unify times (some docs use preferredStartTime/EndTime)
  const fromTime = x.fromTime || x.preferredStartTime || null;
  const toTime = x.toTime || x.preferredEndTime || null;

  return {
    coll,
    id,
    mode: expectedMode,              // "video" | "chat" | "audio"
    userName: x.userName || "—",
    userPhone: x.userPhone || "—",
    mentorId: x.mentorId || x.artistId || "—",
    date: x.date || null,
    fromTime,
    toTime,
    amountPaid: x.amountPaid ?? x.totalPrice ?? 0,
    totalPrice: x.totalPrice ?? x.amountPaid ?? 0,
    status,
    createdAtMs,
  };
}

function sortBookings(list) {
  return [...list].sort((a, b) => {
    const ar = STATUS_RANK[a.status] ?? 99;
    const br = STATUS_RANK[b.status] ?? 99;
    if (ar !== br) return ar - br; // status rank first
    return (b.createdAtMs || 0) - (a.createdAtMs || 0); // newest in same status
  });
}

function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (ts?.toDate) return ts.toDate().getTime();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

function formatDateOnly(v) {
  try {
    if (!v) return "";
    const d = typeof v === "string" ? new Date(v) : (v.toDate ? v.toDate() : new Date(v));
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

function cap(s) {
  try {
    return String(s || "").charAt(0).toUpperCase() + String(s || "").slice(1);
  } catch {
    return s;
  }
}

function formatAmount(n) {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
}

/* ────────────────────────────────────────────────────────── */
/* Styles                                                     */
/* ────────────────────────────────────────────────────────── */
const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 12,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
  flex: 1,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const select = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const selectStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};

const idBtnStyle = {
  border: "none",
  background: "transparent",
  color: COLORS.primary,
  fontWeight: 800,
  cursor: "pointer",
  padding: 0,
};

const primaryBtn = {
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const tdLoading = {
  padding: "14px",
  color: COLORS.subtext,
};

const blurOverlay = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const popupBox = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  width: "90%",
  maxWidth: "400px",
  textAlign: "center",
  boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: "10px 12px",
  color: COLORS.text,
  fontWeight: 700,
  zIndex: 70,
  boxShadow: "0 6px 20px rgba(0,0,0,.12)",
};
